import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Search, Plus, Users, BedDouble, ScanLine, Camera, Sparkles, Loader2, Scale } from 'lucide-react';
import { toast } from 'sonner';
import BarcodeScannerDialog from '@/components/BarcodeScannerDialog';
import FotoPazienteDialog from '@/components/FotoPazienteDialog';
import AssegnaPostiDialog from '@/components/AssegnaPostiDialog';
import RilevazionePesiDialog from '@/components/RilevazionePesiDialog';
import { compressImageForAI } from '@/lib/imageUtils';
import { autoDecodeCF } from '@/lib/cfDecoder';
import { useSede } from '@/lib/sede';
import DatePickerField from '@/components/DatePickerField';

const statusMap = {
  presente: { label: 'Presente', class: 'bg-emerald-100 text-emerald-700' },
  ricoverato: { label: 'Ricoverato', class: 'bg-amber-100 text-amber-700' },
  dimesso: { label: 'Dimesso', class: 'bg-slate-100 text-slate-600' },
  deceduto: { label: 'Deceduto', class: 'bg-gray-100 text-gray-500' },
  trasferito: { label: 'Trasferito', class: 'bg-blue-100 text-brand' },
};

export default function Ospiti() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { sedi, sedeAttiva } = useSede();
  const [ospiti, setOspiti] = useState([]);
  const [search, setSearch] = useState('');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroArea, setFiltroArea] = useState('tutte');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showFoto, setShowFoto] = useState(false);
  const [showPosti, setShowPosti] = useState(false);
  const [showPesi, setShowPesi] = useState(false);
  const [scanningDoc, setScanningDoc] = useState(false);
  const docInputRef = useRef(null);
  const [form, setForm] = useState({ nome: '', cognome: '', codice_fiscale: '', data_nascita: '', sesso: 'M', luogo_nascita: '', indirizzo_residenza: '', data_ingresso: new Date().toISOString().slice(0, 10), stanza: '', letto: '', area: '', sede_id: '', foto_base64: '' });

  const fetchOspiti = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (filtroStato !== 'tutti') params.stato = filtroStato;
    if (filtroArea !== 'tutte') params.area = filtroArea;
    api.get('/ospiti', { params }).then(r => setOspiti(r.data)).catch(() => toast.error('Errore caricamento ospiti')).finally(() => setLoading(false));
  }, [search, filtroStato, filtroArea]);

  useEffect(() => { fetchOspiti(); }, [fetchOspiti]);

  const handleCreate = async () => {
    if (!form.nome || !form.cognome || !form.stanza || !form.letto) { toast.error('Compila i campi obbligatori'); return; }
    try {
      await api.post('/ospiti', { ...form, sede_id: form.sede_id || (sedeAttiva !== 'tutte' ? sedeAttiva : (sedi[0]?.id || '')) });
      toast.success('Ospite creato con successo');
      setShowNew(false);
      setForm({ nome: '', cognome: '', codice_fiscale: '', data_nascita: '', sesso: 'M', luogo_nascita: '', indirizzo_residenza: '', data_ingresso: new Date().toISOString().slice(0, 10), stanza: '', letto: '', area: '', sede_id: '', foto_base64: '' });
      fetchOspiti();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione'); }
  };

  const handleScanResult = (result) => {
    setForm(f => ({
      ...f,
      codice_fiscale: result.codice_fiscale || f.codice_fiscale,
      data_nascita: result.data_nascita || f.data_nascita,
      sesso: result.sesso || f.sesso,
      luogo_nascita: result.luogo_nascita || f.luogo_nascita,
    }));
    const info = [];
    if (result.luogo_nascita) info.push(result.luogo_nascita);
    if (result.eta != null) info.push(`${result.eta} anni`);
    toast.success(`Dati dal CF compilati${info.length ? ': ' + info.join(', ') : ''}`);
  };

  const handlePhotoResult = (photoBase64) => {
    setForm(f => ({ ...f, foto_base64: photoBase64 }));
    toast.success('Foto aggiunta');
  };

  const handleDocScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanningDoc(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const base64 = await compressImageForAI(ev.target.result, 1024, 0.7);
        const res = await api.post('/ospiti/riconosci-documento', { image_base64: base64 });
        if (res.data.parsed && res.data.dati) {
          const d = res.data.dati;
          setForm(f => ({
            ...f,
            nome: d.nome || f.nome,
            cognome: d.cognome || f.cognome,
            codice_fiscale: d.codice_fiscale || f.codice_fiscale,
            data_nascita: d.data_nascita || f.data_nascita,
            sesso: d.sesso || f.sesso,
            luogo_nascita: d.luogo_nascita || f.luogo_nascita,
            indirizzo_residenza: d.indirizzo_residenza || f.indirizzo_residenza,
          }));
          const filled = [d.cognome, d.nome].filter(Boolean).join(' ');
          toast.success(`Dati estratti: ${filled || 'documento letto'}`);
        } else {
          toast.info('Documento non leggibile, compila manualmente');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (err.response?.status === 402 || detail.includes('Credito')) {
          toast.error('Credito AI esaurito. Ricarica in Profilo → Universal Key.');
        } else {
          toast.error('Errore lettura documento');
        }
      }
      setScanningDoc(false);
    };
    reader.readAsDataURL(file);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="ospiti-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Ospiti</h1>
          <p className="text-sm text-slate-500 mt-0.5">{ospiti.length} ospiti trovati</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasRole('coordinatore', 'infermiere', 'medico', 'oss') && (
            <Button variant="outline" onClick={() => setShowPesi(true)} data-testid="rilevazione-pesi-btn">
              <Scale className="w-4 h-4 mr-2" />Rilevazione Peso
            </Button>
          )}
        {hasRole('coordinatore', 'amministrazione', 'medico') && (
          <div className="flex items-center gap-2">
            {hasRole('coordinatore') && (
              <Button variant="outline" onClick={() => setShowPosti(true)} data-testid="assegna-posti-btn">
                <BedDouble className="w-4 h-4 mr-2" />Assegna Posti
              </Button>
            )}
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild>
              <Button data-testid="add-ospite-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-2" />Nuovo Ospite</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Nuovo Ospite</DialogTitle></DialogHeader>
              {/* Photo section */}
              <div className="flex items-center gap-4 pb-3 border-b">
                <div className="relative">
                  {form.foto_base64 ? (
                    <img src={form.foto_base64} alt="Foto" className="w-16 h-16 rounded-full object-cover border-2 border-blue-100" data-testid="new-ospite-foto-preview" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                      <Camera className="w-6 h-6 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <Button variant="outline" size="sm" onClick={() => setShowFoto(true)} data-testid="new-ospite-foto-btn">
                    <Camera className="w-3.5 h-3.5 mr-1.5" />{form.foto_base64 ? 'Cambia Foto' : 'Aggiungi Foto'}
                  </Button>
                  <p className="text-[10px] text-slate-400">Richiede consenso foto</p>
                </div>
              </div>
              {/* Barcode scanner trigger + Document scanner */}
              <div className="flex items-center gap-2 py-2">
                <Button variant="outline" size="sm" onClick={() => setShowScanner(true)} className="flex-1 border-dashed border-blue-300 text-brand hover:bg-blue-50" data-testid="scan-cf-btn">
                  <ScanLine className="w-4 h-4 mr-2" />Scansiona CF (Barcode)
                </Button>
                <input ref={docInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleDocScan} data-testid="scan-doc-input" />
                <Button
                  variant="outline" size="sm"
                  onClick={() => docInputRef.current?.click()}
                  disabled={scanningDoc}
                  className="flex-1 border-dashed border-violet-300 text-violet-600 hover:bg-violet-50"
                  data-testid="scan-doc-btn"
                >
                  {scanningDoc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {scanningDoc ? 'Analisi...' : 'Foto Documento'}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nome *</Label>
                  <Input data-testid="new-ospite-nome" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Cognome *</Label>
                  <Input data-testid="new-ospite-cognome" value={form.cognome} onChange={e => setForm(f => ({ ...f, cognome: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Codice Fiscale</Label>
                  <Input value={form.codice_fiscale} onChange={e => {
                    const val = e.target.value.toUpperCase();
                    setForm(f => ({ ...f, codice_fiscale: val }));
                    autoDecodeCF(val, (r) => {
                      setForm(f => ({ ...f, data_nascita: r.data_nascita || f.data_nascita, sesso: r.sesso || f.sesso, luogo_nascita: r.luogo_nascita || f.luogo_nascita }));
                      toast.success(`CF decodificato: ${r.luogo_nascita || ''}${r.eta != null ? `, ${r.eta} anni` : ''}`);
                    });
                  }} className={form.codice_fiscale ? 'border-emerald-300 bg-emerald-50/50' : ''} placeholder="Inserisci o scansiona barcode" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Data Nascita</Label>
                  <DatePickerField value={form.data_nascita} onChange={v => setForm(f => ({ ...f, data_nascita: v }))} testId="ospite-data-nascita" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Sesso</Label>
                  <Select value={form.sesso} onValueChange={v => setForm(f => ({ ...f, sesso: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="M">Maschio</SelectItem><SelectItem value="F">Femmina</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Luogo di Nascita</Label>
                  <Input value={form.luogo_nascita} onChange={e => setForm(f => ({ ...f, luogo_nascita: e.target.value }))} placeholder="es. Roma (RM)" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs">Indirizzo di Residenza</Label>
                  <Input value={form.indirizzo_residenza} onChange={e => setForm(f => ({ ...f, indirizzo_residenza: e.target.value }))} placeholder="Via, Citta, CAP, Provincia" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Data Ingresso</Label>
                  <DatePickerField value={form.data_ingresso} onChange={v => setForm(f => ({ ...f, data_ingresso: v }))} testId="ospite-data-ingresso" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Stanza *</Label>
                  <Input data-testid="new-ospite-stanza" value={form.stanza} onChange={e => setForm(f => ({ ...f, stanza: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Letto *</Label>
                  <Input data-testid="new-ospite-letto" value={form.letto} onChange={e => setForm(f => ({ ...f, letto: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Area</Label>
                  <Select value={form.area} onValueChange={v => setForm(f => ({ ...f, area: v }))}>
                    <SelectTrigger data-testid="new-ospite-area"><SelectValue placeholder="Seleziona area" /></SelectTrigger>
                    <SelectContent><SelectItem value="verde">Area Verde</SelectItem><SelectItem value="gialla">Area Gialla</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Sede</Label>
                  <Select value={form.sede_id || (sedeAttiva !== 'tutte' ? sedeAttiva : (sedi[0]?.id || ''))}
                    onValueChange={v => setForm(f => ({ ...f, sede_id: v }))}>
                    <SelectTrigger data-testid="new-ospite-sede"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                    <SelectContent>{sedi.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowNew(false)}>Annulla</Button>
                <Button data-testid="save-new-ospite" onClick={handleCreate} className="bg-brand hover:bg-brand-dark text-white">Salva</Button>
              </div>
              <p className="text-[10px] text-slate-400 leading-relaxed mt-2">
                Ai sensi del GDPR (Reg. UE 2016/679) e D.Lgs. 196/2003, i dati personali sono trattati
                per finalita di assistenza sanitaria. L'informativa completa e disponibile presso la struttura.
              </p>
            </DialogContent>
          </Dialog>
          </div>
        )}
        </div>
      </div>

      {/* Rilevazione Peso e Altezze */}
      <RilevazionePesiDialog
        open={showPesi}
        onClose={() => setShowPesi(false)}
        onSaved={fetchOspiti}
      />

      {/* Assegnazione Posti Letto */}
      <AssegnaPostiDialog
        open={showPosti}
        onClose={() => setShowPosti(false)}
        onSaved={fetchOspiti}
      />

      {/* Barcode Scanner Dialog */}
      <BarcodeScannerDialog
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScanResult={handleScanResult}
      />

      {/* Foto Dialog */}
      <FotoPazienteDialog
        open={showFoto}
        onClose={() => setShowFoto(false)}
        onPhotoResult={handlePhotoResult}
      />

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="search-ospiti" placeholder="Cerca per nome, cognome o CF..." className="pl-9 h-10" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filtroStato} onValueChange={setFiltroStato}>
          <SelectTrigger data-testid="filter-stato" className="w-44">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti (attivi)</SelectItem>
            <SelectItem value="presente">Presente</SelectItem>
            <SelectItem value="ricoverato">Ricoverato</SelectItem>
            <SelectItem value="dimesso">Dimesso</SelectItem>
            <SelectItem value="deceduto">Deceduto</SelectItem>
            <SelectItem value="trasferito">Trasferito</SelectItem>
            <SelectItem value="archiviati">Archiviati</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filtroArea} onValueChange={setFiltroArea}>
          <SelectTrigger data-testid="filter-area" className="w-40">
            <SelectValue placeholder="Area" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le aree</SelectItem>
            <SelectItem value="verde">Area Verde</SelectItem>
            <SelectItem value="gialla">Area Gialla</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Patient grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-28" /></Card>)}
        </div>
      ) : ospiti.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun ospite trovato</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="ospiti-grid">
          {ospiti.map(o => {
            const s = statusMap[o.stato] || statusMap.presente;
            return (
              <Card key={o.id} className="card-hover cursor-pointer" onClick={() => navigate(`/ospiti/${o.id}`)} data-testid={`ospite-card-${o.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      {o.foto_base64 && <AvatarImage src={o.foto_base64} alt={`${o.nome} ${o.cognome}`} />}
                      <AvatarFallback className={`text-sm font-semibold ${o.sesso === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                        {o.nome?.[0]}{o.cognome?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{o.cognome} {o.nome}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <BedDouble className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-500">Camera {o.stanza} - Letto {o.letto}</span>
                        {o.area && <Badge className={`text-[10px] px-1.5 py-0 ${o.area === 'verde' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{o.area === 'verde' ? 'Verde' : 'Gialla'}</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={`text-[10px] px-2 py-0.5 ${s.class}`}>{s.label}</Badge>
                        {o.data_nascita && <span className="text-[10px] text-slate-400">{new Date().getFullYear() - new Date(o.data_nascita).getFullYear()} anni</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
